import { addLog } from 'assets/components/feedback/Feedback';
import ModalForm from 'assets/components/form/modalForm/ModalForm';
import ThemeModalFormDefault from 'assets/components/form/modalForm/themes/Theme.ModalForm.Default';
import { getLocales } from 'assets/locales/Locale';
import { combineStrings } from 'assets/utils/data/String';
import { getProductRefundDocumentsFinalFields } from 'assets/utils/other/Utils.Other.GetDocumentFields';
import { isEmail, isValidPhoneNumber } from 'assets/utils/parsersAndValidation/Validators';
import { HttpStatus } from 'config/Api.Config';
import { cloneDeep, merge, set, sum, values } from 'lodash';
import contractRefundGenerateDocumentsApi from 'models/sales/contractRefund/generateDocuments/Api.ContractRefund.GenerateDocuments';
import ContractRefund from 'models/sales/contractRefund/Model.ContractRefund';
import contractRefundSaveApi from 'models/sales/contractRefund/save/Api.ContractRefund.Save';
import contractRefundCommentSaveApi from 'models/sales/contractRefundComment/save/Api.ContractRefundComment.Save';
import contractRefundDocumentSaveApi from 'models/sales/contractRefundDocument/save/Api.ContractRefundDocument.Save';
import Product from 'models/productManagement/product/Model.Product';
import Contract from 'models/sales/contract/Model.Contract';
import { ContractRefundForm, useContractRefundProvider } from '../../Sales.ContractRefund.Hooks';
import SalesContractRefundProcessContract from './tabs/Sales.ContractRefund.Process.Contract';
import SalesContractRefundProcessDistributions from './tabs/Sales.ContractRefund.Process.Distributions';
import SalesContractRefundProcessEndorsements from './tabs/Sales.ContractRefund.Process.Endorsements';
import SalesContractRefundProcessInfo from './tabs/Sales.ContractRefund.Process.Info';
import SalesContractRefundProcessPayees from './tabs/Sales.ContractRefund.Process.Payees';

const SalesContractRefundProcess = () => {
  const { lang } = getLocales();
  const { selected, client, contract, pageRouter, listApi, isRecalculating, originalTotals } =
    useContractRefundProvider();

  return (
    <ModalForm<ContractRefundForm>
      subcomponents={merge(cloneDeep(ThemeModalFormDefault), {
        modal: {
          page: {
            style: {
              pageWrapper: {
                ['--modalWidth']: 'var(--sizeXXL) * 1.5',
              },
            },
          },
        },
      })}
      dependencyArray={[selected, client]}
      title={lang.refundProcess}
      default={{
        ...selected?.toJSON(),
        contractRefundPayees: [{ ...client?.toContractRefundPayee() }],
        contractRefundComments: [],
        contractRefundDocuments: [],
      }}
      onCancel={() => {
        pageRouter.updateParams({});
      }}
      onSubmit={async ({ contractRefundDocuments, contractRefundComments, ...data }) => {
        const res = await contractRefundSaveApi({ ...data, contractRefundComments: [], contractRefundDocuments: [] });
        const contractRefundId = res.payload;

        if (res.status !== HttpStatus.ok) {
          addLog({ error: res.message });
        } else {
          for (const contractRefundComment of contractRefundComments || []) {
            if (contractRefundComment?.comment) {
              await contractRefundCommentSaveApi({
                id: 0,
                contractRefundId: res.payload,
                comment: contractRefundComment.comment,
                userId: contractRefundComment.userId,
              });
            }
          }
          let totalFiles = 0;
          let uploadedFiles = 0;
          for (const contractRefundDocument of contractRefundDocuments || []) {
            if (contractRefundDocument) {
              const docRes = await contractRefundDocumentSaveApi({
                id: 0,
                contractRefundId: res.payload,
                notes: contractRefundDocument.notes,
                name: contractRefundDocument.name,
                formFile: contractRefundDocument.formFile,
              });
              if (docRes.status === HttpStatus.ok) {
                uploadedFiles += 1;
              }
            }
            totalFiles += 1;
          }

          //#region generate docs
          const refetchedContractResponse = await Contract.find({ id: contract.id });
          const refetchedRefundResponse = await ContractRefund.find({ id: contractRefundId });
          const productResponse = await Product.find({ id: contract.productId });
          const refetchedContract = refetchedContractResponse?.payload;
          const refetchedRefund = refetchedRefundResponse?.payload;
          const productDocuments = productResponse?.payload?.productRefundDocuments ?? [];
          const finalFields = getProductRefundDocumentsFinalFields(
            { contract: refetchedContract, contractRefund: refetchedRefund },
            productDocuments,
            []
          );
          await contractRefundGenerateDocumentsApi({
            id: contractRefundId,
            languageIsoCode: refetchedContract.documentLanguageIsoCode,
            productRefundDocumentPdfInputItems: finalFields,
          });
          //#endregion

          if (uploadedFiles !== totalFiles) {
            addLog({
              warning: combineStrings(
                ' ',
                lang.formSuccessAndFilesFailedMessage,
                totalFiles > 0 ? `${uploadedFiles}/${totalFiles} ${lang.filesUploaded}` : null
              ),
            });
          } else {
            addLog({ success: lang.saveSuccess });
          }
          listApi.execute((body) => body);
          pageRouter.updateParams({});
        }
      }}
      validation={(data, errors) => {
        const editedRetainedPricingDistributions = sum(
          values(data.pricingDistributions ?? {})?.map((it) => it.retained?.amount ?? 0)
        );
        const editedRetainedDistributorDistributions = sum(
          values(data.distributorDistributions ?? {})?.map((it) => it.retained?.amount ?? 0)
        );
        const editedRetainedSubfees = sum(values(data.subfees ?? {})?.map((it) => it.retained?.amount ?? 0));

        const editedEnhancements = data.enhancements?.map((enh) => ({
          id: enh.enhancementId,
          retainedPricingDistributions:
            sum(values(enh.pricingDistributions ?? {})?.map((it) => it.retained?.amount ?? 0)) ?? 0,
          retainedDistributorDistributions:
            sum(values(enh.distributorDistributions ?? {})?.map((it) => it.retained?.amount ?? 0)) ?? 0,
        }));

        if (originalTotals.retainedPricingDistributions !== editedRetainedPricingDistributions)
          set(errors, 'retainedPricingDistributions', lang.totalAmountMustMatchOriginal);
        if (originalTotals.retainedDistributorDistributions !== editedRetainedDistributorDistributions)
          set(errors, 'retainedDistributorDistributions', lang.totalAmountMustMatchOriginal);
        if (originalTotals.retainedSubfees !== editedRetainedSubfees)
          set(errors, 'retainedSubfees', lang.totalAmountMustMatchOriginal);
        if (
          !originalTotals.retainedEnhancements?.every((o) => {
            const enh = editedEnhancements?.find((r) => r.id === o.id);
            return (
              enh?.retainedDistributorDistributions === o.retainedDistributorDistributions &&
              enh?.retainedPricingDistributions === o.retainedPricingDistributions
            );
          })
        )
          set(errors, 'retainedEnhancements', lang.totalAmountMustMatchOriginal);

        if (!data.refundDateAtLocal) set(errors, 'refundDateAtLocal', lang.mustNotBeEmpty);
        if (!data.contractCancellation?.odometerReading)
          set(errors, 'contractCancellation.odometerReading', lang.mustNotBeEmpty);
        if (!data.contractCancellation?.cancellationDateLocal)
          set(errors, 'contractCancellation.cancellationDateLocal', lang.mustNotBeEmpty);

        if (
          data?.contractRefundPayees?.some(
            (it) =>
              !it.clientId &&
              !it.dealerId &&
              !it.lenderId &&
              it.payee &&
              (!it.payee.firstName ||
                !it.payee.lastName ||
                !it.payee.streetAddress ||
                !it.payee.city ||
                !it.payee.provinceCode ||
                !it.payee.postalCode ||
                !it.payee.countryCode ||
                !it.payee.paymentMethodCode ||
                !it.payee.email ||
                (it.payee.email && !isEmail(it.payee.email)) ||
                !it.payee.phone ||
                (it.payee.phone && !isValidPhoneNumber(it.payee.phone)))
          )
        ) {
          set(errors, 'contractRefundPayees._objectError', lang.required);
        }
        if (data?.contractRefundPayees?.[0] && !data.contractRefundPayees[0].isPrimary) {
          set(errors, 'contractRefundPayees._objectError', lang.required);
        }
        if (data?.contractRefundPayees?.every((it) => !it.paymentMethodCode)) {
          set(errors, 'contractRefundPayees._objectError', lang.required);
        }
      }}
      tabs={[
        { id: 'info', icon: 'fas-info-circle', text: lang.info, disabled: isRecalculating },
        { id: 'contract', icon: 'fas-file-contract', text: lang.contract, disabled: isRecalculating },
        { id: 'distributions', icon: 'fas-coins', text: lang.distributions, disabled: isRecalculating },
        { id: 'payees', icon: 'fas-user', text: lang.payees, disabled: isRecalculating },
        { id: 'endorsements', icon: 'fas-coins', text: lang.documents, disabled: isRecalculating },
      ]}
      render={({ activeTab }) =>
        activeTab?.id === 'info' ? (
          <SalesContractRefundProcessInfo />
        ) : activeTab?.id === 'contract' ? (
          <SalesContractRefundProcessContract />
        ) : activeTab?.id === 'distributions' ? (
          <SalesContractRefundProcessDistributions />
        ) : activeTab?.id === 'payees' ? (
          <SalesContractRefundProcessPayees />
        ) : activeTab?.id === 'endorsements' ? (
          <SalesContractRefundProcessEndorsements />
        ) : null
      }
      submitButtonText={lang.processRefund}
      disabled={isRecalculating || !(client || contract)}
    />
  );
};

export default SalesContractRefundProcess;
